import React, { useRef } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-unresolved
import Skeleton from 'wsm-skeleton';
import useFragmentLoader from '../hooks/useFragmentLoader';
import { FRAGMENT_STATUS } from '../features/fragmentsSlice';

/**
 * FragmentContent — shared fragment renderer for the mega menu and the drawer.
 *
 * Owns the behavior that is genuinely identical between both call sites:
 * lazy-fetching the fragment via useFragmentLoader, showing a skeleton while
 * loading, injecting sanitized HTML on success, and collapsing silently on
 * error. The two things that legitimately differ between the mega menu and the
 * drawer are injected rather than hard-coded:
 *
 *   - `root`              — the IntersectionObserver root (viewport vs. a real
 *                           scroll container), made explicit at the call site.
 *   - `classNameResolver` — the wrapper class-list policy, resolved from the
 *                           current loader state by each wrapper.
 *
 * @param {object} props
 * @param {string} [props.url] - fragment endpoint URL; when absent nothing renders
 * @param {React.RefObject<HTMLElement> | null} [props.root] - IntersectionObserver
 *   root; null/omitted means viewport-relative
 * @param {(state: { status: string, html: string|null }) => string} props.classNameResolver
 *   - resolves the wrapper element's full className from the current loader state
 * @param {string} [props.skeletonHeight] - height passed to the loading Skeleton
 * @param {boolean} [props.reinitOnReshow=true] - forwarded to useFragmentLoader;
 *   when false, the fragment is not re-initialized each time it becomes visible
 *   again (the mega menu opts out to avoid leaking on every reopen). Defaults to
 *   true so the drawer keeps its remount-driven re-init behavior.
 * @param {string} [props.recVehiclesContext] - when set, stamps
 *   data-rec-vehicles-context on the fragment container so an embedded
 *   ws-rec-vehicles instance can detect the surface it renders in (see
 *   DrawerNavFragment). Omitted by the mega menu, so mega-menu rec-vehicles
 *   keeps its default layout.
 */
const FragmentContent = ({
	url = '',
	root = null,
	classNameResolver,
	skeletonHeight = '12rem',
	reinitOnReshow = true,
	recVehiclesContext = undefined
}) => {
	const slotRef = useRef(null);
	// Pass url||'' to keep hooks unconditional; useFragmentLoader bails via its
	// !el guard when url is absent.
	const { status, html, renderKey } = useFragmentLoader(
		url || '',
		slotRef,
		root,
		{
			reinitOnReshow
		}
	);

	// No URL — nothing to fetch; render nothing (hooks called above to satisfy
	// the Rules of Hooks).
	if (!url) return null;

	let content;
	if (status === FRAGMENT_STATUS.LOADED) {
		// key={renderKey}: forces this DOM to be recreated from scratch whenever
		// useFragmentLoader needs the fragment's script to re-run (see its
		// renderKey docs) — the script only executes its init logic when
		// inserted against markup it hasn't already mutated.
		content = (
			// eslint-disable-next-line react/no-danger
			<div key={renderKey} dangerouslySetInnerHTML={{ __html: html }} />
		);
	} else if (status === FRAGMENT_STATUS.ERROR) {
		content = null;
	} else {
		// idle or loading — show skeleton
		content = <Skeleton height={skeletonHeight} width="100%" />;
	}

	const isLoading =
		status === FRAGMENT_STATUS.IDLE || status === FRAGMENT_STATUS.LOADING;

	return (
		<div
			ref={slotRef}
			className={classNameResolver({ status, html })}
			data-testid="fragment-slot"
			data-rec-vehicles-context={recVehiclesContext}
			aria-live="polite"
			aria-busy={isLoading}
		>
			{content}
		</div>
	);
};

FragmentContent.propTypes = {
	url: PropTypes.string,
	root: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
	classNameResolver: PropTypes.func.isRequired,
	skeletonHeight: PropTypes.string,
	reinitOnReshow: PropTypes.bool,
	recVehiclesContext: PropTypes.string
};

export default FragmentContent;
